import React from "react";
import { LegalPage, LegalSection, LegalList } from "../components/SiteShell";
import { usePageMeta } from "../lib/usePageMeta";

export default function Privacy() {
  usePageMeta({
    title: "Privacy Policy",
    description: "How Fitnudge collects, uses, and protects gym, member, and lead information. Gym owners retain ownership of their data - Fitnudge is the software platform.",
  });

  return (
    <LegalPage
      testid="privacy-page"
      title="Privacy Policy"
      subtitle="How we collect, use, and protect information when you use Fitnudge."
      lastUpdated="30 May 2026"
    >
      <p>
        Welcome to Fitnudge. Fitnudge ("we", "our", "us") provides software that
        helps fitness businesses manage leads, members, communication, onboarding,
        payments, and operational workflows. This Privacy Policy explains how we
        collect, use, and protect information when you use Fitnudge.
      </p>

      <LegalSection title="Account Information">
        <p>We collect the following when you create or sign in to a Fitnudge account:</p>
        <LegalList items={[
          "Name",
          "Email address",
          "Phone number",
          "Gym / business name",
          "Login credentials",
        ]}/>
      </LegalSection>

      <LegalSection title="Gym & Business Data">
        <p>Information entered by gym owners, including:</p>
        <LegalList items={[
          "Leads",
          "Members",
          "Attendance records",
          "Membership records",
          "Payment tracking information",
          "Waiver status",
          "Notes and communications",
        ]}/>
      </LegalSection>

      <LegalSection title="Member Information">
        <p>Gym owners may store the following member details:</p>
        <LegalList items={[
          "Name",
          "Phone number",
          "Email address",
          "Date of birth",
          "Emergency contacts",
          "Blood group",
          "Fitness goals",
          "Membership details",
        ]}/>
      </LegalSection>

      <LegalSection title="Lead Data">
        <p>
          Lead pipeline information - contact details, trial dates, follow-up
          notes, and status changes - is stored to power the Fitnudge CRM, lead
          stages, and automated reminders configured by the gym.
        </p>
      </LegalSection>

      <LegalSection title="Payment Tracking Information">
        <p>
          Fitnudge stores membership-payment metadata (amount, plan, due date,
          payment status) entered by gym owners. Payment <em>card</em> data is
          never stored on Fitnudge - card collection is handled by Razorpay (for
          Fitnudge subscriptions) or by your own payment processor for member
          billing.
        </p>
      </LegalSection>

      <LegalSection title="WhatsApp Integration">
        <p>
          Certain features (lead nudges, renewal reminders, click-to-chat) open
          WhatsApp using deep links. Your member's phone number is shared with
          WhatsApp/Meta only when you initiate a conversation. Fitnudge does not
          read your WhatsApp messages.
        </p>
      </LegalSection>

      <LegalSection title="Google Login">
        <p>
          If you sign in with Google, we receive your name, email address, and
          profile picture from Google. We use this strictly to authenticate you
          and create your Fitnudge account.
        </p>
      </LegalSection>

      <LegalSection title="AI Features">
        <p>
          Fitnudge generates daily tasks, operational suggestions, and member
          insights using AI models. Only the data needed for the specific
          insight (e.g., a member's attendance pattern) is sent to the AI model
          for that request. Outputs are advisory and should be reviewed before
          acting on them.
        </p>
      </LegalSection>

      <LegalSection title="Third-Party Services">
        <p>Fitnudge may integrate with:</p>
        <LegalList items={[
          "WhatsApp Business Platform",
          "Meta Platforms",
          "Google Authentication",
          "Razorpay",
          "Jotform",
          "Supabase",
          "Analytics providers",
        ]}/>
        <p>These providers may process information according to their own privacy policies.</p>
      </LegalSection>

      <LegalSection title="Cookies & Analytics">
        <p>
          Fitnudge uses cookies and similar technologies for session management,
          authentication, and basic analytics (page views, feature usage) to
          improve the product. You can disable cookies in your browser, though
          parts of Fitnudge may not function correctly.
        </p>
      </LegalSection>

      <LegalSection title="How We Use Information">
        <LegalList items={[
          "Provide and improve Fitnudge",
          "Manage user accounts",
          "Deliver support",
          "Send service notifications",
          "Generate analytics and AI insights",
          "Maintain platform security",
        ]}/>
      </LegalSection>

      <LegalSection title="Data Ownership">
        <p>
          Gym owners retain ownership of their business and member data.
          Fitnudge acts solely as a software platform - we process data on your
          behalf to provide the service.
        </p>
      </LegalSection>

      <LegalSection title="Data Security">
        <p>
          We implement reasonable technical and organisational measures to
          protect data, including encryption in transit, isolated databases per
          gym tenancy, and access controls. However, no method of transmission
          or storage is completely secure.
        </p>
      </LegalSection>

      <LegalSection title="User Responsibilities">
        <p>
          Gym owners are responsible for obtaining the necessary permissions and
          consent from their members before collecting or storing personal
          information in Fitnudge.
        </p>
      </LegalSection>

      <LegalSection title="Data Retention">
        <p>
          We retain information only for as long as necessary to provide our
          services and comply with legal obligations. You may request export or
          deletion of your gym's data at any time by writing to{" "}
          <a href="mailto:support@fitnudge.in" className="text-primary hover:underline">support@fitnudge.in</a>.
        </p>
      </LegalSection>

      <LegalSection title="Contact">
        <p>
          For privacy-related inquiries:{" "}
          <a href="mailto:support@fitnudge.in" className="text-primary hover:underline">support@fitnudge.in</a>
        </p>
      </LegalSection>
    </LegalPage>
  );
}
